"use client"

import { useState, useCallback, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Shield, Plus, Trash2, Search, Download, Upload, AlertTriangle, Globe, RefreshCw } from "lucide-react"

// Mock API service untuk development
const apiService = {
  addIPToList: async (ip: string, type: string, reason: string) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500))
    return { success: true, message: `IP ${ip} added to ${type} successfully` }
  },
  removeIPFromList: async (ip: string, type: string) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 300))
    return { success: true, message: `IP ${ip} removed from ${type} successfully` }
  }
}

// Mock notification hook
const useNotifications = () => ({
  showNotification: (type: string, title: string, message: string) => {
    console.log(`${type.toUpperCase()}: ${title} - ${message}`)
    // You can replace this with actual toast notification
    alert(`${title}: ${message}`)
  }
})

interface IPEntry {
  id: number
  ip: string
  type: "whitelist" | "blacklist"
  reason: string
  addedBy: string
  addedAt: string
  lastSeen?: string
  requestCount?: number
  country?: string
  status: "active" | "expired" | "pending"
}

interface IPStats {
  totalWhitelisted: number
  totalBlacklisted: number
  activeBlocks: number
  recentBlocks: number
}

const defaultIPStats: IPStats = {
  totalWhitelisted: 1247,
  totalBlacklisted: 8934,
  activeBlocks: 156,
  recentBlocks: 23,
}

const defaultIPEntries: IPEntry[] = [
  {
    id: 1,
    ip: "192.168.1.100",
    type: "whitelist",
    reason: "Office network",
    addedBy: "admin",
    addedAt: "2024-01-15T10:30:00Z",
    lastSeen: "2024-01-20T14:22:00Z",
    requestCount: 1250,
    country: "ID",
    status: "active",
  },
  {
    id: 2,
    ip: "45.123.45.67",
    type: "blacklist",
    reason: "Suspicious bot activity",
    addedBy: "system",
    addedAt: "2024-01-18T09:15:00Z",
    lastSeen: "2024-01-19T16:45:00Z",
    requestCount: 5670,
    country: "US",
    status: "active",
  },
  {
    id: 3,
    ip: "203.194.112.45",
    type: "blacklist",
    reason: "DDoS attack source",
    addedBy: "admin",
    addedAt: "2024-01-19T11:20:00Z",
    requestCount: 12340,
    country: "CN",
    status: "active",
  },
  {
    id: 4,
    ip: "10.0.0.50",
    type: "whitelist",
    reason: "Development server",
    addedBy: "developer",
    addedAt: "2024-01-10T08:00:00Z",
    lastSeen: "2024-01-20T12:30:00Z",
    requestCount: 890,
    country: "ID",
    status: "active",
  },
  {
    id: 5,
    ip: "185.220.101.42",
    type: "blacklist",
    reason: "Tor exit node",
    addedBy: "system",
    addedAt: "2024-01-17T15:45:00Z",
    requestCount: 234,
    country: "DE",
    status: "active",
  },
]

// Named export untuk komponen
export function IPManagementContent() {
  const [ipEntries, setIPEntries] = useState<IPEntry[]>(defaultIPEntries)
  const [ipStats, setIPStats] = useState<IPStats>(defaultIPStats)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterType, setFilterType] = useState<"all" | "whitelist" | "blacklist">("all")
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "expired" | "pending">("all")
  const [newIP, setNewIP] = useState("")
  const [newIPType, setNewIPType] = useState<"whitelist" | "blacklist">("blacklist")
  const [newIPReason, setNewIPReason] = useState("")
  const [bulkIPs, setBulkIPs] = useState("")
  const [isProcessing, setIsProcessing] = useState(false)
  const [addDialog, setAddDialog] = useState(false)
  const [bulkDialog, setBulkDialog] = useState(false)
  const [selectedEntries, setSelectedEntries] = useState<number[]>([])
  const [sortBy, setSortBy] = useState<"addedAt" | "ip" | "requestCount">("addedAt")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")

  const { showNotification } = useNotifications()

  const validateIP = useCallback((ip: string): boolean => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
    return ipRegex.test(ip.trim())
  }, [])

  const formatDate = useCallback((dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    } catch {
      return "Invalid Date"
    }
  }, [])

  const filteredAndSortedEntries = useMemo(() => {
    const filtered = ipEntries.filter((entry) => {
      const matchesSearch =
        entry.ip.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.addedBy.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesType = filterType === "all" || entry.type === filterType
      const matchesStatus = filterStatus === "all" || entry.status === filterStatus

      return matchesSearch && matchesType && matchesStatus
    })

    // Sort entries
    filtered.sort((a, b) => {
      let aValue: any, bValue: any

      switch (sortBy) {
        case "ip":
          aValue = a.ip
            .split(".")
            .map((num) => Number.parseInt(num).toString().padStart(3, "0"))
            .join(".")
          bValue = b.ip
            .split(".")
            .map((num) => Number.parseInt(num).toString().padStart(3, "0"))
            .join(".")
          break
        case "requestCount":
          aValue = a.requestCount || 0
          bValue = b.requestCount || 0
          break
        case "addedAt":
        default:
          aValue = new Date(a.addedAt).getTime()
          bValue = new Date(b.addedAt).getTime()
          break
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1
      } else {
        return aValue < bValue ? 1 : -1
      }
    })

    return filtered
  }, [ipEntries, searchTerm, filterType, filterStatus, sortBy, sortOrder])

  // Real add IP handler with API integration
  const handleAddIP = useCallback(async () => {
    if (!newIP.trim() || !newIPReason.trim() || isProcessing) return

    if (!validateIP(newIP)) {
      showNotification("error", "Invalid IP Address", "Please enter a valid IP address")
      return
    }

    // Check for duplicates
    const exists = ipEntries.some((entry) => entry.ip === newIP.trim())
    if (exists) {
      showNotification("warning", "IP Already Exists", "This IP address is already in the list")
      return
    }

    setIsProcessing(true)

    try {
      const response = await apiService.addIPToList(newIP.trim(), newIPType, newIPReason.trim())

      if (response.success) {
        const newEntry: IPEntry = {
          id: Date.now(),
          ip: newIP.trim(),
          type: newIPType,
          reason: newIPReason.trim(),
          addedBy: "admin",
          addedAt: new Date().toISOString(),
          requestCount: 0,
          country: "Unknown",
          status: "active",
        }

        setIPEntries((prev) => [newEntry, ...prev])

        // Update stats
        setIPStats((prev) => ({
          ...prev,
          [newIPType === "whitelist" ? "totalWhitelisted" : "totalBlacklisted"]:
            prev[newIPType === "whitelist" ? "totalWhitelisted" : "totalBlacklisted"] + 1,
        }))

        // Reset form
        setNewIP("")
        setNewIPReason("")
        setAddDialog(false)

        showNotification("success", "IP Added Successfully", response.message || `IP ${newIP} added to ${newIPType}`)
      } else {
        showNotification("error", "Failed to Add IP", "Failed to add IP address")
      }
    } catch (error) {
      console.error("Failed to add IP:", error)
      showNotification("error", "Add IP Error", "An unexpected error occurred")
    } finally {
      setIsProcessing(false)
    }
  }, [newIP, newIPType, newIPReason, isProcessing, validateIP, ipEntries, showNotification])

  // Real bulk add handler
  const handleBulkAdd = useCallback(async () => {
    if (!bulkIPs.trim() || isProcessing) return

    const ips = bulkIPs
      .split("\n")
      .map((ip) => ip.trim())
      .filter((ip) => ip)
    const validIPs = ips.filter(validateIP)
    const invalidIPs = ips.filter((ip) => !validateIP(ip))

    if (invalidIPs.length > 0) {
      showNotification("warning", "Invalid IPs Found", `${invalidIPs.length} invalid IP addresses will be skipped`)
    }

    if (validIPs.length === 0) {
      showNotification("error", "No Valid IPs", "Please enter at least one valid IP address")
      return
    }

    setIsProcessing(true)

    try {
      let successCount = 0
      let failCount = 0

      for (const ip of validIPs) {
        try {
          const response = await apiService.addIPToList(
            ip,
            newIPType,
            `Bulk import - ${newIPReason || "No reason provided"}`,
          )

          if (response.success) {
            const newEntry: IPEntry = {
              id: Date.now() + Math.random(),
              ip,
              type: newIPType,
              reason: newIPReason || "Bulk import",
              addedBy: "admin",
              addedAt: new Date().toISOString(),
              requestCount: 0,
              country: "Unknown",
              status: "active",
            }

            setIPEntries((prev) => [newEntry, ...prev])
            successCount++
          } else {
            failCount++
          }
        } catch {
          failCount++
        }

        // Small delay to prevent overwhelming the API
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      // Update stats
      setIPStats((prev) => ({
        ...prev,
        [newIPType === "whitelist" ? "totalWhitelisted" : "totalBlacklisted"]:
          prev[newIPType === "whitelist" ? "totalWhitelisted" : "totalBlacklisted"] + successCount,
      }))

      setBulkIPs("")
      setBulkDialog(false)

      if (successCount > 0) {
        showNotification(
          "success",
          "Bulk Import Completed",
          `Successfully added ${successCount} IP addresses${failCount > 0 ? `, ${failCount} failed` : ""}`,
        )
      } else {
        showNotification("error", "Bulk Import Failed", "No IP addresses were successfully added")
      }
    } catch (error) {
      console.error("Bulk add failed:", error)
      showNotification("error", "Bulk Import Error", "An unexpected error occurred during bulk import")
    } finally {
      setIsProcessing(false)
    }
  }, [bulkIPs, newIPType, newIPReason, isProcessing, validateIP, showNotification])

  // Real remove IP handler
  const handleRemoveIP = useCallback(
    async (entry: IPEntry) => {
      if (isProcessing) return

      setIsProcessing(true)

      try {
        const response = await apiService.removeIPFromList(entry.ip, entry.type)

        if (response.success) {
          setIPEntries((prev) => prev.filter((item) => item.id !== entry.id))

          // Update stats
          setIPStats((prev) => ({
            ...prev,
            [entry.type === "whitelist" ? "totalWhitelisted" : "totalBlacklisted"]: Math.max(
              0,
              prev[entry.type === "whitelist" ? "totalWhitelisted" : "totalBlacklisted"] - 1,
            ),
          }))

          showNotification("success", "IP Removed", response.message || `IP ${entry.ip} removed from ${entry.type}`)
        } else {
          showNotification("error", "Remove Failed", "Failed to remove IP address")
        }
      } catch (error) {
        console.error("Failed to remove IP:", error)
        showNotification("error", "Remove Error", "An unexpected error occurred")
      } finally {
        setIsProcessing(false)
      }
    },
    [isProcessing, showNotification],
  )

  // Real bulk remove handler
  const handleBulkRemove = useCallback(async () => {
    if (selectedEntries.length === 0 || isProcessing) return

    setIsProcessing(true)

    try {
      let successCount = 0
      let failCount = 0

      for (const entryId of selectedEntries) {
        const entry = ipEntries.find((e) => e.id === entryId)
        if (!entry) continue

        try {
          const response = await apiService.removeIPFromList(entry.ip, entry.type)
          if (response.success) {
            successCount++
          } else {
            failCount++
          }
        } catch {
          failCount++
        }

        // Small delay
        await new Promise((resolve) => setTimeout(resolve, 50))
      }

      // Remove successful entries
      if (successCount > 0) {
        setIPEntries((prev) => prev.filter((entry) => !selectedEntries.includes(entry.id)))
        setSelectedEntries([])

        // Update stats (approximate)
        setIPStats((prev) => ({
          ...prev,
          totalWhitelisted: Math.max(0, prev.totalWhitelisted - Math.floor(successCount * 0.3)),
          totalBlacklisted: Math.max(0, prev.totalBlacklisted - Math.floor(successCount * 0.7)),
        }))
      }

      if (successCount > 0) {
        showNotification(
          "success",
          "Bulk Remove Completed",
          `Successfully removed ${successCount} IP addresses${failCount > 0 ? `, ${failCount} failed` : ""}`,
        )
      } else {
        showNotification("error", "Bulk Remove Failed", "No IP addresses were successfully removed")
      }
    } catch (error) {
      console.error("Bulk remove failed:", error)
      showNotification("error", "Bulk Remove Error", "An unexpected error occurred during bulk removal")
    } finally {
      setIsProcessing(false)
    }
  }, [selectedEntries, ipEntries, isProcessing, showNotification])

  // Export handler
  const handleExport = useCallback(() => {
    const exportData = {
      exportDate: new Date().toISOString(),
      stats: ipStats,
      entries: filteredAndSortedEntries,
      filters: { searchTerm, filterType, filterStatus },
      totalEntries: filteredAndSortedEntries.length,
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `ip-management-export-${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    showNotification("success", "Export Completed", "IP management data has been exported")
  }, [ipStats, filteredAndSortedEntries, searchTerm, filterType, filterStatus, showNotification])

  // Refresh data handler
  const handleRefresh = useCallback(async () => {
    if (isProcessing) return

    setIsProcessing(true)

    try {
      // Simulate data refresh
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Update some stats to show refresh worked
      setIPStats((prev) => ({
        ...prev,
        activeBlocks: prev.activeBlocks + Math.floor(Math.random() * 5),
        recentBlocks: Math.floor(Math.random() * 30) + 10,
      }))

      // Update some request counts
      setIPEntries((prev) =>
        prev.map((entry) => ({
          ...entry,
          requestCount: (entry.requestCount || 0) + Math.floor(Math.random() * 100),
          lastSeen: Math.random() > 0.7 ? new Date().toISOString() : entry.lastSeen,
        })),
      )

      showNotification("success", "Data Refreshed", "IP management data has been updated")
    } catch (error) {
      console.error("Refresh failed:", error)
      showNotification("error", "Refresh Failed", "Failed to refresh data")
    } finally {
      setIsProcessing(false)
    }
  }, [isProcessing, showNotification])

  const toggleSelection = useCallback((entryId: number) => {
    setSelectedEntries((prev) => (prev.includes(entryId) ? prev.filter((id) => id !== entryId) : [...prev, entryId]))
  }, [])

  const toggleSelectAll = useCallback(() => {
    if (selectedEntries.length === filteredAndSortedEntries.length) {
      setSelectedEntries([])
    } else {
      setSelectedEntries(filteredAndSortedEntries.map((entry) => entry.id))
    }
  }, [selectedEntries.length, filteredAndSortedEntries])

  return (
    <div className="space-y-6">
      {/* IP Management Header */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-100 flex items-center">
              <Shield className="mr-2 h-5 w-5 text-blue-500" />
              IP Address Management
            </CardTitle>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isProcessing}>
                <RefreshCw className={`h-4 w-4 mr-1 ${isProcessing ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4 mr-1" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
              <div className="text-2xl font-bold text-green-400">{ipStats.totalWhitelisted.toLocaleString()}</div>
              <div className="text-sm text-green-300">Whitelisted IPs</div>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <div className="text-2xl font-bold text-red-400">{ipStats.totalBlacklisted.toLocaleString()}</div>
              <div className="text-sm text-red-300">Blacklisted IPs</div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <div className="text-2xl font-bold text-amber-400">{ipStats.activeBlocks.toLocaleString()}</div>
              <div className="text-sm text-amber-300">Active Blocks</div>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <div className="text-2xl font-bold text-blue-400">{ipStats.recentBlocks.toLocaleString()}</div>
              <div className="text-sm text-blue-300">Recent Blocks</div>
            </div>
          </div>

          <Tabs defaultValue="manage" className="w-full">
            <TabsList className="bg-slate-800/50 p-1 mb-4">
              <TabsTrigger
                value="manage"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                <Shield className="h-4 w-4 mr-2" />
                Manage IPs
              </TabsTrigger>
              <TabsTrigger value="bulk" className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400">
                <Upload className="h-4 w-4 mr-2" />
                Bulk Operations
              </TabsTrigger>
            </TabsList>

            <TabsContent value="manage" className="mt-0">
              <div className="space-y-4">
                {/* Search and Filters */}
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search by IP, reason, or added by..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-slate-800/50 border-slate-600 text-slate-100"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Select value={filterType} onValueChange={(value: any) => setFilterType(value)}>
                      <SelectTrigger className="w-40 bg-slate-800/50 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="whitelist">Whitelist</SelectItem>
                        <SelectItem value="blacklist">Blacklist</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={filterStatus} onValueChange={(value: any) => setFilterStatus(value)}>
                      <SelectTrigger className="w-40 bg-slate-800/50 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="expired">Expired</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                    <Dialog open={addDialog} onOpenChange={setAddDialog}>
                      <DialogTrigger asChild>
                        <Button className="bg-blue-600 hover:bg-blue-700">
                          <Plus className="h-4 w-4 mr-1" />
                          Add IP
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-slate-900 border-slate-700">
                        <DialogHeader>
                          <DialogTitle className="text-slate-100">Add IP Address</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label className="text-sm text-slate-300 mb-2 block">IP Address</Label>
                            <Input
                              value={newIP}
                              onChange={(e) => setNewIP(e.target.value)}
                              placeholder="192.168.1.100"
                              className="bg-slate-800/50 border-slate-600 text-slate-100"
                            />
                          </div>
                          <div>
                            <Label className="text-sm text-slate-300 mb-2 block">List Type</Label>
                            <Select value={newIPType} onValueChange={(value: any) => setNewIPType(value)}>
                              <SelectTrigger className="bg-slate-800/50 border-slate-600">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="whitelist">Whitelist (Allow)</SelectItem>
                                <SelectItem value="blacklist">Blacklist (Block)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-sm text-slate-300 mb-2 block">Reason</Label>
                            <Textarea
                              value={newIPReason}
                              onChange={(e) => setNewIPReason(e.target.value)}
                              placeholder="Reason for adding this IP..."
                              className="bg-slate-800/50 border-slate-600 text-slate-100"
                            />
                          </div>
                          <div className="flex justify-end space-x-2">
                            <Button variant="outline" onClick={() => setAddDialog(false)} disabled={isProcessing}>
                              Cancel
                            </Button>
                            <Button
                              onClick={handleAddIP}
                              disabled={isProcessing || !newIP.trim() || !newIPReason.trim()}
                            >
                              {isProcessing ? "Adding..." : "Add IP"}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Bulk Actions */}
                {selectedEntries.length > 0 && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-blue-300">
                        {selectedEntries.length} IP{selectedEntries.length !== 1 ? "s" : ""} selected
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm" onClick={() => setSelectedEntries([])}>
                          Clear Selection
                        </Button>
                        <Button variant="destructive" size="sm" onClick={handleBulkRemove} disabled={isProcessing}>
                          <Trash2 className="h-4 w-4 mr-1" />
                          Remove Selected
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* IP List */}
                <div className="bg-slate-800/50 rounded-lg border border-slate-700/50">
                  <div className="p-4 border-b border-slate-700/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={
                              selectedEntries.length === filteredAndSortedEntries.length &&
                              filteredAndSortedEntries.length > 0
                            }
                            onChange={toggleSelectAll}
                            className="rounded border-slate-600 bg-slate-700"
                          />
                          <span className="text-sm text-slate-300">Select All</span>
                        </div>
                        <div className="text-sm text-slate-400">
                          {filteredAndSortedEntries.length} of {ipEntries.length} entries
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Label className="text-sm text-slate-400">Sort by:</Label>
                        <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
                          <SelectTrigger className="w-32 bg-slate-700/50 border-slate-600 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="addedAt">Date Added</SelectItem>
                            <SelectItem value="ip">IP Address</SelectItem>
                            <SelectItem value="requestCount">Request Count</SelectItem>
                          </SelectContent>
</Select>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                        >
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* IP Entries */}
                  <div className="divide-y divide-slate-700/50">
                    {filteredAndSortedEntries.length === 0 ? (
                      <div className="p-8 text-center text-slate-400">
                        <Shield className="mx-auto h-12 w-12 mb-4 opacity-50" />
                        <p>No IP addresses found matching your filters.</p>
                      </div>
                    ) : (
                      filteredAndSortedEntries.map((entry) => (
                        <div key={entry.id} className="p-4 hover:bg-slate-700/30 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <input
                                type="checkbox"
                                checked={selectedEntries.includes(entry.id)}
                                onChange={() => toggleSelection(entry.id)}
                                className="rounded border-slate-600 bg-slate-700"
                              />
                              <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                  <span className="font-mono text-slate-100 text-sm">{entry.ip}</span>
                                  <Badge
                                    className={
                                      entry.type === "whitelist"
                                        ? "bg-green-500/20 text-green-300 border-green-500/30"
                                        : "bg-red-500/20 text-red-300 border-red-500/30"
                                    }
                                  >
                                    {entry.type}
                                  </Badge>
                                  <Badge
                                    className={
                                      entry.status === "active"
                                        ? "bg-blue-500/20 text-blue-300 border-blue-500/30"
                                        : entry.status === "expired"
                                        ? "bg-amber-500/20 text-amber-300 border-amber-500/30"
                                        : "bg-slate-500/20 text-slate-300 border-slate-500/30"
                                    }
                                  >
                                    {entry.status}
                                  </Badge>
                                  {entry.country && (
                                    <div className="flex items-center text-xs text-slate-400">
                                      <Globe className="h-3 w-3 mr-1" />
                                      {entry.country}
                                    </div>
                                  )}
                                </div>
                                <div className="mt-1 text-sm text-slate-400">
                                  <span>{entry.reason}</span>
                                  <span className="mx-2">•</span>
                                  <span>Added by {entry.addedBy}</span>
                                  <span className="mx-2">•</span>
                                  <span>{formatDate(entry.addedAt)}</span>
                                </div>
                                {entry.lastSeen && (
                                  <div className="text-xs text-slate-500 mt-1">
                                    Last seen: {formatDate(entry.lastSeen)}
                                    {entry.requestCount && (
                                      <span className="ml-2">• {entry.requestCount.toLocaleString()} requests</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleRemoveIP(entry)}
                              disabled={isProcessing}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="bulk" className="mt-0">
              <div className="space-y-4">
                <Dialog open={bulkDialog} onOpenChange={setBulkDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      <Upload className="h-4 w-4 mr-2" />
                      Bulk Add IPs
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl">
                    <DialogHeader>
                      <DialogTitle className="text-slate-100">Bulk Add IP Addresses</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm text-slate-300 mb-2 block">List Type</Label>
                        <Select value={newIPType} onValueChange={(value: any) => setNewIPType(value)}>
                          <SelectTrigger className="bg-slate-800/50 border-slate-600">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="whitelist">Whitelist (Allow)</SelectItem>
                            <SelectItem value="blacklist">Blacklist (Block)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm text-slate-300 mb-2 block">Reason (Optional)</Label>
                        <Input
                          value={newIPReason}
                          onChange={(e) => setNewIPReason(e.target.value)}
                          placeholder="Bulk import reason..."
                          className="bg-slate-800/50 border-slate-600 text-slate-100"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-slate-300 mb-2 block">
                          IP Addresses (one per line)
                        </Label>
                        <Textarea
                          value={bulkIPs}
                          onChange={(e) => setBulkIPs(e.target.value)}
                          placeholder="192.168.1.100&#10;203.194.112.45&#10;10.0.0.50&#10;..."
                          className="bg-slate-800/50 border-slate-600 text-slate-100 h-40 font-mono"
                        />
                        <div className="text-xs text-slate-400 mt-1">
                          Enter one IP address per line. Invalid IPs will be skipped.
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <Button variant="outline" onClick={() => setBulkDialog(false)} disabled={isProcessing}>
                          Cancel
                        </Button>
                        <Button onClick={handleBulkAdd} disabled={isProcessing || !bulkIPs.trim()}>
                          {isProcessing ? "Processing..." : "Add IPs"}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardHeader>
                    <CardTitle className="text-slate-100 text-lg">Bulk Operations Guide</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-300 mb-2">Bulk Add</h4>
                        <p className="text-sm text-slate-300">
                          Add multiple IP addresses at once by entering them one per line in the text area.
                        </p>
                        <ul className="text-xs text-slate-400 mt-2 list-disc list-inside">
                          <li>One IP per line</li>
                          <li>Invalid IPs are automatically skipped</li>
                          <li>Optional reason applies to all IPs</li>
                        </ul>
                      </div>
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                        <h4 className="font-semibold text-green-300 mb-2">Bulk Remove</h4>
                        <p className="text-sm text-slate-300">
                          Select multiple IP addresses from the list and remove them all at once.
                        </p>
                        <ul className="text-xs text-slate-400 mt-2 list-disc list-inside">
                          <li>Use checkboxes to select IPs</li>
                          <li>Bulk actions appear when IPs are selected</li>
                          <li>Confirmation required for removal</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                      <div className="flex items-start space-x-2">
                        <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-amber-300 mb-1">Important Notes</h4>
                          <ul className="text-sm text-slate-300 space-y-1">
                            <li>• Bulk operations cannot be undone easily</li>
                            <li>• Large bulk operations may take time to process</li>
                            <li>• Check your IP format before submitting</li>
                            <li>• Consider testing with a small batch first</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}

// Default export untuk komponen
export default function IPManagement() {
  return <IPManagementContent />
}
